import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSignOut } from '../hooks/useSignOut';
import { useUserProfile } from '../context/UserProfileContext';
import { resolveUserDisplayName, resolveUserInitials } from '../utils/userDisplayName';
import { useClubBranding } from '../hooks/useClubBranding';
import { C, alpha, useTranslationTyped } from '@kantera/ui';
import type { Role } from '@kantera/types';
import { useProfileViewContext } from '../context/ProfileViewContext';
import { isChildLogin } from '../utils/userRoles';
import { OrgSwitchOverlay } from './OrgSwitchOverlay';

// ---------------------------------------------------------------------------
// Inline SVG icon primitives — consistent 16x16 viewBox, currentColor stroke
// ---------------------------------------------------------------------------
// The app's own icon set. A few are exported for reuse outside the nav (the setup screen's three
// choices, KAN-118) so the app keeps one look rather than mixing in emoji or a second icon package.

const IconDashboard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IconPlayers = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="2" />
    <path
      d="M3 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="18" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M15 20c0-2.485 1.567-4.5 3.5-4.5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

export const IconTeams = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 3c0 0 3 3.5 3 9s-3 9-3 9M12 3c0 0-3 3.5-3 9s3 9 3 9M3 12h18"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M4.5 7.5h15M4.5 16.5h15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconMatches = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 14h4M8 17h2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconSeasons = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="15" r="2" stroke="currentColor" strokeWidth="1.75" />
  </svg>
);

const IconLeagues = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3l2.09 6.26H21l-5.45 3.98 2.09 6.26L12 15.52l-5.64 4.02 2.09-6.26L3 9.26h6.91L12 3z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const IconStandings = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="3" rx="1" stroke="currentColor" strokeWidth="1.75" />
    <rect x="3" y="9" width="14" height="3" rx="1" stroke="currentColor" strokeWidth="1.75" />
    <rect x="3" y="15" width="10" height="3" rx="1" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M19 14l2 2-2 2M17 16h4"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconTraining = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M13 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" fill="currentColor" />
    <path
      d="M12 6l-2 5h4l-1 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 10l-2 2M16 10l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconLeaderboard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="14" width="5" height="8" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="9.5" y="9" width="5" height="13" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="17" y="4" width="5" height="18" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IconConsent = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path
      d="M7 11V7a5 5 0 0 1 10 0v4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

export const IconClubs = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 21h18M3 10l9-7 9 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="9" y="14" width="6" height="7" stroke="currentColor" strokeWidth="2" />
    <rect x="5" y="10" width="4" height="5" stroke="currentColor" strokeWidth="1.75" />
    <rect x="15" y="10" width="4" height="5" stroke="currentColor" strokeWidth="1.75" />
  </svg>
);

const IconAnalytics = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline
      points="22 12 18 12 15 21 9 3 6 12 2 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconGamification = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCompliance = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L4 5v6c0 5.25 3.5 10.15 8 11.25C16.5 21.15 20 16.25 20 11V5l-8-3z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconBilling = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M2 10h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 15h4M14 15h2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconStaff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <path
      d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconEmailDeliveries = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** KAN-202: erasure requests — a document with a strike through it. */
const IconErasureRequests = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconVenues = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L2 7l10 5 10-5-10-5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M2 17l10 5 10-5M2 12l10 5 10-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const IconSettings = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// Tactical Lab icons
// ---------------------------------------------------------------------------

const IconTacticsLibrary = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {/* Pitch outline */}
    <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.75" />
    {/* Centre line */}
    <line
      x1="12"
      y1="4"
      x2="12"
      y2="20"
      stroke="currentColor"
      strokeWidth="1"
      strokeDasharray="2 2"
    />
    {/* Play triangle overlay */}
    <path d="M9 10l6 2-6 2V10z" fill="currentColor" opacity="0.9" />
  </svg>
);

const IconFormations = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {/* Pitch background hint */}
    <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
    {/* Formation dots — 4-3-3 shape */}
    {/* GK */}
    <circle cx="12" cy="18" r="1.5" fill="currentColor" />
    {/* Defenders */}
    <circle cx="6" cy="15" r="1.5" fill="currentColor" />
    <circle cx="10" cy="15" r="1.5" fill="currentColor" />
    <circle cx="14" cy="15" r="1.5" fill="currentColor" />
    <circle cx="18" cy="15" r="1.5" fill="currentColor" />
    {/* Midfielders */}
    <circle cx="8" cy="11" r="1.5" fill="currentColor" />
    <circle cx="12" cy="11" r="1.5" fill="currentColor" />
    <circle cx="16" cy="11" r="1.5" fill="currentColor" />
    {/* Forwards */}
    <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    <circle cx="12" cy="7" r="1.5" fill="currentColor" />
    <circle cx="17" cy="7" r="1.5" fill="currentColor" />
  </svg>
);

// ---------------------------------------------------------------------------
// New section icons
// ---------------------------------------------------------------------------

const IconSkillAssessments = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 11l3 3L22 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconTrainingPlans = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <path
      d="M7 8h10M7 12h10M7 16h6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const IconPlayerDevelopment = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 20V10M18 20V4M6 20v-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconReports = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="14 2 14 8 20 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IconVideoAnalysis = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="6" width="15" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
    <path
      d="M17 9l5-3v12l-5-3V9z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconHighlights = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polygon
      points="5 3 19 12 5 21 5 3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconPhotos = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2" />
    <path
      d="M21 15l-5-5L5 21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconMessaging = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCoachChannel = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L2 7l10 5 10-5-10-5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 17l10 5 10-5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 12l10 5 10-5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCourses = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3L2 9l10 6 10-6-10-6z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 17l10 6 10-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 13l10 6 10-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconNutrition = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M18 8h1a4 4 0 0 1 0 8h-1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6 1v3M10 1v3M14 1v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const IconImport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="17 8 12 3 7 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="3"
      x2="12"
      y2="15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconTacticalBoard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="16" cy="8" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="8" cy="16" r="1.5" fill="currentColor" />
    <circle cx="16" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

const IconHelpCenter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="17" r="1" fill="currentColor" />
  </svg>
);

const IconFamily = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="2" />
    <path
      d="M3 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="18" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M15 20c0-2.485 1.567-4.5 3.5-4.5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

// Wave 6: Player Portal icons
const IconMyProfile = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
    <path
      d="M4 21c0-3.866 3.582-7 8-7s8 3.134 8 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconSchedule = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <line
      x1="16"
      y1="2"
      x2="16"
      y2="6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const IconAchievements = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9 20l3-4 3 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 12V8M10 10l2-2 2 2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const IconHomework = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 19.5A2.5 2.5 0 016.5 17H20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <line
      x1="9"
      y1="7"
      x2="15"
      y2="7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="9"
      y1="11"
      x2="13"
      y2="11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const IconChallenges = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6-6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconSafeguarding = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C17.5 22.15 20 17.25 20 12V6l-8-4z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// KanteraLogo intentionally removed — the TopBar carries the workspace header
// (club pill + profile-view switcher); the sidebar is nav only.

// Sign-out icon
const IconSignOut = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="16 17 21 12 16 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="21"
      y1="12"
      x2="9"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// KI-012 AI Tools icons
// ---------------------------------------------------------------------------

const IconScoutingReports = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 11h6M11 8v6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

// ---------------------------------------------------------------------------
// Nav structure — grouped sections with role-based visibility
// ---------------------------------------------------------------------------

/**
 * `roles` — if undefined, the item is visible to all authenticated users.
 * If specified, the user must have at least one matching role (owner always
 * has the same access as admin).
 */
interface NavItem {
  /** i18n key suffix under webSidebar namespace, e.g. "navDashboard" */
  labelKey: string;
  path: string;
  Icon: React.FC;
  roles?: Role[];
  /**
   * Hidden from a child's own login (isChildLogin, utils/userRoles), whatever `roles` says and in
   * every hat: for pages the API refuses to such a login outright (KAN-221, match footage).
   */
  exceptChildLogin?: boolean;
  /** When true, only highlight this item when the path matches exactly */
  exact?: boolean;
}

interface NavSection {
  /** i18n key suffix under webSidebar namespace, e.g. "sectionCore" */
  titleKey: string;
  items: NavItem[];
}

const IconDiscovery = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9.5 9.5l5 2-2 5-5-2 2-5z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

const ADMIN_COACH: Role[] = ['admin', 'coach', 'owner'];
const ADMIN_ONLY: Role[] = ['admin', 'owner'];
const PARENT_ONLY: Role[] = ['parent'];
const PLAYER_ONLY: Role[] = ['player'];
const COACH_ONLY: Role[] = ['coach', 'owner'];
const REFEREE_ONLY: Role[] = ['referee'];
const TEAM_MANAGER_ONLY: Role[] = ['team_manager'];
/** Matches ProtectedRoute requiredRole={['admin','parent']} on /discovery/controls. */
const ADMIN_PARENT: Role[] = ['admin', 'owner', 'parent'];

const NAV_SECTIONS: NavSection[] = [
  {
    titleKey: 'sectionCore',
    items: [
      { labelKey: 'navDashboard', path: '/', Icon: IconDashboard, roles: ADMIN_COACH },
      { labelKey: 'navPlayers', path: '/players', Icon: IconPlayers, roles: ADMIN_COACH },
      { labelKey: 'navTeams', path: '/teams', Icon: IconTeams, roles: ADMIN_COACH },
      { labelKey: 'navMatches', path: '/matches', Icon: IconMatches, roles: ADMIN_COACH },
    ],
  },
  {
    titleKey: 'sectionDevelopment',
    items: [
      // REMOVED — 'Tactical Board' pointed at /formation, which renders a page
      // titled "Formations". Taylor: "Tactical board sidebar button just links to
      // the fomrations side bar button. Tactical board is suppsoed to do exactly
      // what i just described about transitional formations... like how coaches
      // use a magnetic white baord."
      //
      // That board does not exist in apps/web. /formation is a pass-through
      // gallery over the same /api/formations/encyclopedia endpoint as
      // /tactics/formations, and its click handler takes no argument, so it
      // discards which formation was clicked. A nav entry whose page is not built
      // does not appear in the nav — it comes back when the board does.
      {
        labelKey: 'navSkillAssessments',
        path: '/skill-assessments',
        Icon: IconSkillAssessments,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navTrainingPlans',
        path: '/training-plans',
        Icon: IconTrainingPlans,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navPlayerDevelopment',
        path: '/player-development',
        Icon: IconPlayerDevelopment,
        roles: ADMIN_COACH,
      },
      { labelKey: 'navReports', path: '/reports', Icon: IconReports, roles: ADMIN_COACH },
    ],
  },
  {
    titleKey: 'sectionAiTools',
    items: [
      {
        labelKey: 'navScoutingReports',
        path: '/scouting-reports',
        Icon: IconScoutingReports,
        roles: ADMIN_COACH,
      },
      // REMOVED — 'Game Strategy' pointed at /matches, the SAME path as the
      // 'Matches' entry and with the same roles. Taylor: "game strategy sidebar
      // just goes to /matches so its a duplicate."
      //
      // The R03 note this replaces was an improvement on a 404, but two rows
      // sharing one destination is still a lie: both highlight together (isActive
      // is a prefix match) and one of them never leads where its label says.
      // Game Strategy is match-scoped (/matches/:id/game-strategy) and belongs as
      // a TAB on the match page — see the match sub-navigation work — not as a
      // top-level destination.
    ],
  },
  {
    titleKey: 'sectionTacticalLab',
    items: [
      {
        labelKey: 'navTacticsLibrary',
        path: '/tactics',
        Icon: IconTacticsLibrary,
        exact: true,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navFormations',
        path: '/tactics/formations',
        Icon: IconFormations,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navTacticsEditor',
        path: '/tactics/editor',
        Icon: IconTacticalBoard,
        roles: ADMIN_COACH,
      },
    ],
  },
  {
    titleKey: 'sectionMedia',
    items: [
      {
        labelKey: 'navVideoAnalysis',
        path: '/video-analysis',
        Icon: IconVideoAnalysis,
        roles: ADMIN_COACH,
      },
      { labelKey: 'navHighlights', path: '/highlights', Icon: IconHighlights, roles: ADMIN_COACH },
      // Every login but a child's: the API refuses match photos to one (KAN-221).
      { labelKey: 'navPhotos', path: '/photos', Icon: IconPhotos, exceptChildLogin: true },
    ],
  },
  {
    titleKey: 'sectionCommunication',
    items: [
      {
        labelKey: 'navMessaging',
        path: '/messaging',
        Icon: IconMessaging,
        roles: [...ADMIN_COACH, 'parent'],
      },
      {
        labelKey: 'navDirectMessages',
        path: '/messages/direct',
        Icon: IconMessaging,
        roles: [...ADMIN_COACH, 'parent'],
      },
      { labelKey: 'navNotifications', path: '/notifications', Icon: IconSchedule },
      {
        labelKey: 'navCoachChannel',
        path: '/coach-channel',
        Icon: IconCoachChannel,
        roles: COACH_ONLY,
      },
    ],
  },
  {
    titleKey: 'sectionEducation',
    items: [
      { labelKey: 'navCourses', path: '/courses', Icon: IconCourses },
      { labelKey: 'navNutrition', path: '/nutrition', Icon: IconNutrition },
    ],
  },
  {
    // D-004 Discovery — flag-gated (default OFF); pages render a notice when flag is OFF
    titleKey: 'sectionDiscovery',
    items: [
      {
        labelKey: 'navDiscoveryFeed',
        path: '/discovery/feed',
        Icon: IconDiscovery,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navDiscoveryControls',
        path: '/discovery/controls',
        Icon: IconDiscovery,
        // Was ADMIN_COACH_PARENT, but the route is requiredRole={['admin','parent']}
        // — so every coach who clicked this was redirected to the Dashboard.
        // Narrowed to match the route rather than widening the route: these
        // controls govern a parent's choice about their own child's
        // discoverability, which is not a coach's decision.
        roles: ADMIN_PARENT,
      },
      // D-005 Comparison — flag-gated (default OFF)
      {
        labelKey: 'navPlayerComparison',
        path: '/comparison',
        Icon: IconDiscovery,
        roles: ADMIN_COACH,
      },
    ],
  },
  {
    titleKey: 'sectionOperations',
    items: [
      { labelKey: 'navSeasons', path: '/seasons', Icon: IconSeasons, roles: ADMIN_COACH },
      { labelKey: 'navLeagues', path: '/leagues', Icon: IconLeagues, roles: ADMIN_COACH },
      {
        labelKey: 'navLeagueScheduling',
        path: '/league-scheduling',
        Icon: IconMatches,
        roles: ADMIN_ONLY,
      },
      { labelKey: 'navStandings', path: '/standings', Icon: IconStandings },
      {
        labelKey: 'navTournaments',
        path: '/tournaments',
        Icon: IconLeaderboard,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navSeasonArchive',
        path: '/season-archive',
        Icon: IconSeasons,
        roles: ADMIN_COACH,
      },
      { labelKey: 'navSessions', path: '/training', Icon: IconTraining, roles: ADMIN_COACH },
      { labelKey: 'navTryouts', path: '/tryouts', Icon: IconPlayers, roles: ADMIN_COACH },
    ],
  },
  {
    titleKey: 'sectionAdmin',
    items: [
      { labelKey: 'navStaff', path: '/staff', Icon: IconStaff, roles: ADMIN_ONLY },
      { labelKey: 'navVenues', path: '/venues', Icon: IconVenues, roles: ADMIN_ONLY },
      {
        labelKey: 'navEmailDeliveries',
        path: '/email-deliveries',
        Icon: IconEmailDeliveries,
        roles: ADMIN_ONLY,
      },
      {
        labelKey: 'navErasureRequests',
        path: '/erasure-requests',
        Icon: IconErasureRequests,
        roles: ADMIN_ONLY,
      },
      { labelKey: 'navImportData', path: '/import', Icon: IconImport, roles: ADMIN_ONLY },
      { labelKey: 'navOnboarding', path: '/onboarding', Icon: IconImport, roles: ADMIN_ONLY },
      {
        labelKey: 'navLeaderboard',
        path: '/leaderboard',
        Icon: IconLeaderboard,
        roles: ADMIN_COACH,
      },
      {
        labelKey: 'navGamification',
        path: '/gamification',
        Icon: IconGamification,
        roles: ADMIN_COACH,
      },
      { labelKey: 'navConsent', path: '/consent', Icon: IconConsent, roles: PARENT_ONLY },
      { labelKey: 'navClubs', path: '/clubs', Icon: IconClubs, roles: ADMIN_ONLY },
      { labelKey: 'navAnalytics', path: '/analytics', Icon: IconAnalytics, roles: ADMIN_COACH },
      { labelKey: 'navCompliance', path: '/compliance', Icon: IconCompliance, roles: ADMIN_ONLY },
      {
        labelKey: 'navSafeguarding',
        path: '/safeguarding',
        Icon: IconSafeguarding,
        roles: ADMIN_ONLY,
      },
      { labelKey: 'navBilling', path: '/billing', Icon: IconBilling, roles: ADMIN_ONLY },
      {
        labelKey: 'navRegistrationForms',
        path: '/registration/forms',
        Icon: IconHomework,
        roles: ADMIN_ONLY,
      },
      {
        labelKey: 'navRegistrationMgmt',
        path: '/registration/manage',
        Icon: IconHomework,
        roles: ADMIN_ONLY,
      },
      { labelKey: 'navSettings', path: '/settings', Icon: IconSettings },
      { labelKey: 'navAppearance', path: '/settings/appearance', Icon: IconSettings },
    ],
  },
  {
    titleKey: 'sectionReferee',
    items: [
      {
        labelKey: 'navRefereeToday',
        path: '/referee',
        Icon: IconMatches,
        roles: REFEREE_ONLY,
        exact: true,
      },
      // REMOVED — 'Upcoming' pointed at /referee?tab=upcoming, but
      // RefereeDashboard.tsx never reads a `tab` search param (no
      // useSearchParams anywhere in the file). It rendered the IDENTICAL page as
      // 'Today' above, and could never show as active because 'Today' claims
      // /referee with exact:true.
      //
      // GAP, not a fix: referees have no upcoming-fixtures view. Re-add this row
      // when RefereeDashboard actually honours the tab.
      {
        labelKey: 'navRefereeIncidentLog',
        path: '/referee/incidents',
        Icon: IconReports,
        roles: REFEREE_ONLY,
      },
    ],
  },
  {
    titleKey: 'sectionTeamManager',
    items: [
      {
        labelKey: 'navTeamManagerDashboard',
        path: '/team-manager',
        Icon: IconDashboard,
        roles: TEAM_MANAGER_ONLY,
        exact: true,
      },
      // Days 2-7 ship the rest of these surfaces (Communications, RSVPs,
      // Volunteers, Notifications). Hidden until their pages exist so the
      // experience reads intentional rather than half-built.
    ],
  },
  {
    titleKey: 'sectionParent',
    items: [
      { labelKey: 'navMyChildren', path: '/my-children', Icon: IconFamily, roles: PARENT_ONLY },
      { labelKey: 'navFamilyCircle', path: '/family-circle', Icon: IconFamily, roles: PARENT_ONLY },
      { labelKey: 'navFamilyView', path: '/family/view', Icon: IconFamily, roles: PARENT_ONLY },
      {
        labelKey: 'navFamilySchedule',
        path: '/family/schedule',
        Icon: IconSchedule,
        roles: PARENT_ONLY,
      },
      {
        labelKey: 'navFamilyStandings',
        path: '/family/standings',
        Icon: IconStandings,
        roles: PARENT_ONLY,
      },
      {
        labelKey: 'navFamilyBadges',
        path: '/family/badges',
        Icon: IconAchievements,
        roles: PARENT_ONLY,
      },
      // REMOVED — 'My Schedule' (PARENT_ONLY -> /my-schedule): the route is wrapped
      // in PlayerSelfRoute, which redirects anyone without the 'player' role to
      // the Dashboard. Every parent who clicked this was silently bounced. The
      // correct entry for that route already exists as navPlayerSchedule
      // (PLAYER_ONLY) in sectionPlayerPortal below, so this was a duplicate
      // pointing at a guard it could never pass.
      //
      // REMOVED — 'Billing' (PARENT_ONLY -> /billing): /billing is the CLUB-ADMIN
      // subscription page, gated ProtectedRoute requiredRole="admin", so parents
      // were redirected before it mounted. It was also a second nav entry for the
      // same path as navBilling (ADMIN_ONLY, above).
      //
      // GAP, not a fix: parents have no fees/payments page at all. Re-add a row
      // here when one exists — pointed at that page, not at the club's Stripe
      // subscription.
    ],
  },
  {
    titleKey: 'sectionPlayerPortal',
    items: [
      { labelKey: 'navMyProfile', path: '/my-profile', Icon: IconMyProfile, roles: PLAYER_ONLY },
      {
        labelKey: 'navPlayerSchedule',
        path: '/my-schedule',
        Icon: IconSchedule,
        roles: PLAYER_ONLY,
      },
      { labelKey: 'navMyTeam', path: '/my-team', Icon: IconTeams, roles: PLAYER_ONLY },
      {
        labelKey: 'navAchievements',
        path: '/my-achievements',
        Icon: IconAchievements,
        roles: PLAYER_ONLY,
      },
      { labelKey: 'navHomework', path: '/my-homework', Icon: IconHomework, roles: PLAYER_ONLY },
      { labelKey: 'navChallenges', path: '/challenges', Icon: IconChallenges, roles: PLAYER_ONLY },
    ],
  },
  {
    titleKey: 'sectionHelp',
    items: [{ labelKey: 'navHelpCenter', path: '/help-center', Icon: IconHelpCenter }],
  },
];

// ---------------------------------------------------------------------------
// Platform nav — shown when user has founder or platform_admin role
// ---------------------------------------------------------------------------

const IconAllClubs = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 21h18M3 10l9-7 9 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="9" y="14" width="6" height="7" stroke="currentColor" strokeWidth="2" />
    <rect x="5" y="10" width="4" height="5" stroke="currentColor" strokeWidth="1.75" />
    <rect x="15" y="10" width="4" height="5" stroke="currentColor" strokeWidth="1.75" />
  </svg>
);

const IconGrowth = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline
      points="23 6 13.5 15.5 8.5 10.5 1 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="17 6 23 6 23 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconRevenue = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line
      x1="12"
      y1="1"
      x2="12"
      y2="23"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconUsage = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
    <path
      d="M14 17.5h2M16 17.5V14M20 17.5h.01M17.5 20v.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconViewAsClub = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IconAddClub = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PLATFORM_NAV_SECTIONS: NavSection[] = [
  {
    titleKey: 'sectionPlatform',
    items: [
      { labelKey: 'navPlatformDashboard', path: '/platform', Icon: IconDashboard, exact: true },
      { labelKey: 'navAllClubs', path: '/platform/clubs', Icon: IconAllClubs },
      { labelKey: 'navAddClub', path: '/platform/clubs/new', Icon: IconAddClub },
      { labelKey: 'navInviteClubAdmin', path: '/platform/invite', Icon: IconPlayers },
      { labelKey: 'navWaitlist', path: '/platform/waitlist', Icon: IconEmailDeliveries },
      { labelKey: 'navPlatformLeaderboard', path: '/leaderboard', Icon: IconLeaderboard },
    ],
  },
  {
    titleKey: 'sectionAnalytics',
    items: [
      { labelKey: 'navProductAnalytics', path: '/platform/analytics', Icon: IconAnalytics },
      { labelKey: 'navGrowth', path: '/platform/growth', Icon: IconGrowth },
      { labelKey: 'navRevenue', path: '/platform/revenue', Icon: IconRevenue },
      { labelKey: 'navMarketing', path: '/platform/marketing', Icon: IconUsage },
      { labelKey: 'navFinance', path: '/platform/finance', Icon: IconBilling },
    ],
  },
  {
    titleKey: 'sectionInternalTools',
    items: [
      { labelKey: 'navEngineering', path: '/platform/engineering', Icon: IconSettings },
      { labelKey: 'navFeatureFlags', path: '/platform/feature-flags', Icon: IconSettings },
      { labelKey: 'navSupport', path: '/platform/support', Icon: IconHelpCenter },
      { labelKey: 'navContent', path: '/platform/content', Icon: IconCourses },
      { labelKey: 'navCostModel', path: '/platform/cost-model', Icon: IconBilling },
    ],
  },
  {
    titleKey: 'sectionTools',
    items: [
      { labelKey: 'navViewAsClub', path: '/clubs', Icon: IconViewAsClub },
      { labelKey: 'navPlatformSettings', path: '/settings', Icon: IconSettings },
    ],
  },
];

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

// Section header style is now inline in the nav rendering for per-section control

// ---------------------------------------------------------------------------
// Sidebar component
// ---------------------------------------------------------------------------

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, switchingOrgName, tenant } = useAuth();
  const { profile } = useUserProfile();
  // Same precedence as TopBar and Dashboard — see utils/userDisplayName. The
  // API mirrors the profile name into user_metadata, but only on PATCH, so a
  // profile row created any other way (invite acceptance, seed, direct DB) has
  // no mirrored name and every consumer of user.name fell back to the email.
  const sidebarUserName =
    resolveUserDisplayName({ profile, metadataName: user?.name, email: user?.email }) ?? '';
  const { logoUrl: clubLogoUrl, primaryColor } = useClubBranding();
  const { t } = useTranslationTyped();

  // Which hat is the user wearing right now. A person who is owner + coach +
  // parent holds all those roles at once; the view selector lets them focus one
  // at a time. This changes only what the sidebar shows, never their access —
  // the API still authorizes by the full JWT role set.
  const { activeView, effectiveRoles } = useProfileViewContext();
  const inPlatformView = activeView.id === 'platform';
  // Read from the token, not the hat: access follows the full token, whichever hat is on.
  const childLogin = isChildLogin(tenant?.roles ?? []);

  // In platform view, use indigo as the primary color and the Kantera brand.
  const effectivePrimaryColor = inPlatformView ? '#818cf8' : primaryColor;
  const effectiveLogoUrl = inPlatformView ? null : clubLogoUrl;

  // Platform view uses a different nav structure.
  const activeSections = inPlatformView ? PLATFORM_NAV_SECTIONS : NAV_SECTIONS;

  /**
   * Returns true if the nav item is visible for the ACTIVE VIEW's roles.
   * Items with no `roles` restriction are always visible. In platform view all
   * platform nav items are visible. An `exceptChildLogin` item is never shown
   * to a child's own login.
   */
  const isVisible = (item: NavItem): boolean => {
    if (item.exceptChildLogin && childLogin) return false;
    if (inPlatformView) return true;
    if (!item.roles) return true;
    return item.roles.some((r) => effectiveRoles.includes(r));
  };

  const isActive = (item: NavItem) => {
    if (item.path === '/') return location.pathname === '/';
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const handleSignOut = useSignOut();

  // Active role display — platform admin and no-role states are translated;
  // named roles are capitalized at runtime (not in the translation files) since
  // they come from the DB and aren't enumerated in the sidebar namespace.
  const roleLabel = inPlatformView
    ? t('webSidebar.platformAdminRole')
    : effectiveRoles[0]
      ? effectiveRoles[0].charAt(0).toUpperCase() + effectiveRoles[0].slice(1)
      : t('webSidebar.defaultRole');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <OrgSwitchOverlay visible={switchingOrgName !== null} orgName={switchingOrgName ?? ''} />

      {/* Sidebar atmosphere — faded repeating club logo wallpaper */}
      {effectiveLogoUrl && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.045,
            backgroundImage: `url(${effectiveLogoUrl})`,
            backgroundSize: '48px 48px',
            backgroundRepeat: 'repeat',
            transform: 'rotate(-15deg) scale(1.4)',
            pointerEvents: 'none',
            zIndex: 0,
            filter: 'grayscale(1)',
          }}
        />
      )}

      {/* Subtle gradient atmosphere — color tint at top */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 50% 0%, ${alpha(effectivePrimaryColor, 0.04)}, transparent 50%),
            linear-gradient(to bottom, ${alpha(effectivePrimaryColor, 0.02)} 0%, transparent 30%, transparent 80%, ${alpha(effectivePrimaryColor, 0.01)} 100%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Noise/grain texture for depth */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Club identity + workspace/profile switching now live in the top bar
          (org+team picker on the club pill, view switcher on the role pill).
          The duplicate sidebar block was removed. */}

      {/* ─── NAVIGATION ─── */}
      <nav
        aria-label={t('webSidebar.navAriaLabel')}
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingBottom: '8px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {activeSections.map((section) => {
          const visibleItems = section.items.filter(isVisible);
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.titleKey}>
              {/* Section header with gradient accent line */}
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: C.text.muted,
                  padding: '14px 14px 5px',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>{t(`webSidebar.${section.titleKey}`)}</span>
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    background: `linear-gradient(90deg, ${C.text.faint}, transparent)`,
                  }}
                />
              </div>

              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '1px', padding: '0 6px' }}
              >
                {visibleItems.map((item) => {
                  const active = isActive(item);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        color: active ? C.text.primary : C.text.secondary,
                        textDecoration: 'none',
                        transition: 'all 0.15s ease',
                        background: active
                          ? `${alpha(effectivePrimaryColor, 0.07)}`
                          : 'transparent',
                        fontWeight: active ? '600' : '400',
                        fontSize: '13px',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Active indicator bar */}
                      {active && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '20%',
                            bottom: '20%',
                            width: '3px',
                            borderRadius: '0 2px 2px 0',
                            background: `linear-gradient(to bottom, ${effectivePrimaryColor}, ${alpha(effectivePrimaryColor, 0.5)})`,
                            boxShadow: `0 0 8px ${alpha(effectivePrimaryColor, 0.38)}`,
                          }}
                        />
                      )}
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          color: active ? effectivePrimaryColor : 'inherit',
                          opacity: active ? 1 : 0.6,
                          transition: 'opacity 0.15s ease',
                        }}
                      >
                        <item.Icon />
                      </span>
                      <span>{t(`webSidebar.${item.labelKey}`)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* ─── USER INFO ─── */}
      {user && (
        <div
          style={{
            padding: '12px',
            borderTop: `1px solid ${C.border}`,
            background: `linear-gradient(to top, ${C.bg.card}, transparent)`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* User avatar — photo when set, initial fallback otherwise */}
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: profile?.avatarUrl
                  ? `center / cover no-repeat url(${profile.avatarUrl})`
                  : `linear-gradient(135deg, ${alpha(effectivePrimaryColor, 0.19)}, ${alpha(effectivePrimaryColor, 0.06)})`,
                border: `1.5px solid ${alpha(effectivePrimaryColor, 0.25)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 700,
                color: effectivePrimaryColor,
                flexShrink: 0,
              }}
            >
              {profile?.avatarUrl
                ? ''
                : resolveUserInitials({
                    profile,
                    metadataName: user?.name,
                    email: user?.email,
                  }) || 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: C.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sidebarUserName || user.email}
              </div>
              <div
                style={{
                  fontSize: '10px',
                  color: C.text.muted,
                  textTransform: 'capitalize',
                  marginTop: '1px',
                }}
              >
                {roleLabel}
              </div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            aria-label={t('webSidebar.signOutAriaLabel')}
            style={{
              marginTop: '10px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '7px 0',
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${C.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              color: C.text.muted,
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'inherit',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.border = '1px solid rgba(239,68,68,0.3)';
              e.currentTarget.style.color = '#f87171';
              e.currentTarget.style.background = 'rgba(239,68,68,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.border = `1px solid ${C.border}`;
              e.currentTarget.style.color = C.text.muted;
              e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
            }}
          >
            <IconSignOut />
            <span>{t('webSidebar.signOut')}</span>
          </button>
        </div>
      )}
    </div>
  );
};
